'use client';

import { useState } from 'react';
import { useRouter } from '@/i18n/routing';
import { createJob, updateJob } from "@/lib/actions/jobs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ArrowLeft, Save, Plus, Trash2 } from "lucide-react";
import { Link } from "@/i18n/routing";

import { RichTextEditor } from "../shared/RichTextEditor";
import { parseJsonField } from '@/lib/utils';

interface JobFormProps {
  initialData?: any;
}

export function JobForm({ initialData }: JobFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const isEditing = !!initialData;

  const [formData, setFormData] = useState<{
    title: string;
    slug: string;
    location: string;
    shortDescription: string;
    description: string;
    isActive: boolean;
    applicationLink: string;
    requirements: string[];
  }>({
    title: initialData?.title || '',
    slug: initialData?.slug || '',
    location: initialData?.location || 'Jakarta, Indonesia',
    shortDescription: initialData?.shortDescription || '',
    description: initialData?.description || '',
    isActive: initialData?.isActive ?? true,
    applicationLink: initialData?.applicationLink || '',
    requirements: parseJsonField(initialData?.requirements, []),
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const newData = { ...prev, [name]: value };
      if (name === 'title' && !isEditing) {
        newData.slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      }
      return newData;
    });
  };

  const handleManualChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (checked: boolean) => {
    setFormData((prev) => ({ ...prev, isActive: checked }));
  };

  const handleArrayChange = (index: number, value: string) => {
    setFormData((prev) => {
      const newReqs = [...prev.requirements];
      newReqs[index] = value;
      return { ...prev, requirements: newReqs };
    });
  };

  const addRequirement = () => {
    setFormData((prev) => ({
      ...prev,
      requirements: [...prev.requirements, '']
    }));
  };

  const removeRequirement = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      requirements: prev.requirements.filter((_: any, i: number) => i !== index)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const result = isEditing 
        ? await updateJob(initialData.id, formData)
        : await createJob(formData);

      if (result.success) {
        toast.success(`Job position ${isEditing ? 'updated' : 'created'} successfully`);
        router.push('/admin/careers');
      } else {
        toast.error(result.error || "Something went wrong");
      }
    } catch (error) {
      toast.error("Failed to save job position");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" asChild>
          <Link href="/admin/careers">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <h1 className="text-3xl font-bold text-navy">
          {isEditing ? 'Edit Position' : 'New Position'}
        </h1>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 pb-20">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Job Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title</Label>
                  <Input id="title" name="title" value={formData.title} onChange={handleChange} placeholder="e.g. Strategic Consultant" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="slug">Slug</Label>
                  <Input id="slug" name="slug" value={formData.slug} onChange={handleChange} placeholder="e.g. strategic-consultant" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="shortDescription">Short Description</Label>
                  <RichTextEditor value={formData.shortDescription} onChange={(val) => handleManualChange('shortDescription', val)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Full Description & Responsibilities</Label>
                  <RichTextEditor value={formData.description} onChange={(val) => handleManualChange('description', val)} />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Requirements</CardTitle>
                <Button type="button" variant="outline" size="sm" onClick={addRequirement}>
                  <Plus className="h-4 w-4 mr-2" /> Add Req
                </Button>
              </CardHeader>
              <CardContent className="space-y-3">
                {formData.requirements.map((req: string, index: number) => (
                  <div key={index} className="flex gap-2">
                    <Input value={req} onChange={(e) => handleArrayChange(index, e.target.value)} placeholder="Enter requirement..." />
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeRequirement(index)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-1 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Recruitment Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <Input id="location" name="location" value={formData.location} onChange={handleChange} placeholder="e.g. Jakarta, Indonesia" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="applicationLink">Application URL (External)</Label>
                  <Input id="applicationLink" name="applicationLink" value={formData.applicationLink} onChange={handleChange} placeholder="https://..." />
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                  <div className="space-y-0.5">
                    <Label className="text-sm font-medium">Active Status</Label>
                    <p className="text-xs text-gray-500">Visible on site</p>
                  </div>
                  <Switch checked={formData.isActive} onCheckedChange={handleSwitchChange} />
                </div>
              </CardContent>
            </Card>

            <Button type="submit" className="w-full bg-navy hover:bg-primary" disabled={isLoading}>
              {isLoading ? 'Saving...' : <><Save className="mr-2 h-4 w-4" /> Save Position</>}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
